import { useState } from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import BookConsultationDialog from "@/components/site/BookConsultationDialog";
import JoinUsDialog from "@/components/site/JoinUsDialog";
import FloatingActions from "@/components/site/FloatingActions";
import EditorialHero from "@/components/site/v2/EditorialHero";
import ChallengeCards from "@/components/site/v2/ChallengeCards";
import ServiceExplorer from "@/components/site/v2/ServiceExplorer";
import FrameworkDiagram from "@/components/site/v2/FrameworkDiagram";
import OutcomeStrip from "@/components/site/v2/OutcomeStrip";
import ImpactStrip from "@/components/site/v2/ImpactStrip";
import FaqSection from "@/components/site/v2/FaqSection";
import RelatedLinks from "@/components/site/v2/RelatedLinks";
import DiagnosticOffer from "@/components/site/v2/DiagnosticOffer";
import ContextualCTA from "@/components/site/v2/ContextualCTA";
import ImageBand from "@/components/site/v2/ImageBand";
import { track } from "@/lib/analytics";
import { usePageMeta } from "@/lib/usePageMeta";
import { imageForChallenge, serviceImagePool, imageForPath, uniquePicker, ALL_IMAGES } from "@/data/images";
import { PRACTICE_SEO } from "@/data/seoMap";
import RelatedInsights from "@/components/site/v2/RelatedInsights";
import { PRACTICE_INSIGHTS } from "@/data/insightsMap";
import LeadMagnetSection from "@/components/site/v2/LeadMagnetSection";
import LeadMagnetDialog from "@/components/site/LeadMagnetDialog";
import { PRACTICE_LEAD_MAGNET } from "@/data/leadMagnets";
import ToolPromo from "@/components/tools/ToolPromo";

const BAND_IMAGES = {
  "/capabilities/strategy-commercial-transformation": "/photos/practice-band-strategy.jpg",
  "/capabilities/market-customer-intelligence": "/photos/practice-band-intelligence.jpg",
  "/capabilities/brand-marketing-communications": "/photos/practice-band-brand.jpg",
  "/capabilities/digital-commerce": "/photos/practice-band-digital.jpg",
};

/**
 * Shared V2 template for the four core practice pages.
 * Hero → challenges → capability explorer → framework → outcomes →
 * impact (optional) → related → FAQ → CTA.
 */
export default function PracticePageV2({
  testid,
  page,
  capUrl,
  image,
  imageAlt,
  heroDescription,
  heroTagline,
  challenges,
  explorerItems,
  deliverables,
  outcomes,
  impact,
  impactNote,
  frameworkSteps,
  frameworkHeadline,
  frameworkKicker,
  methods,
}) {
  const [bookOpen, setBookOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [magnetOpen, setMagnetOpen] = useState(false);
  const pseo = PRACTICE_SEO[capUrl.split("/").pop()] || {};
  const practiceMagnet = PRACTICE_LEAD_MAGNET[capUrl.split("/").pop()];

  usePageMeta({
    title: pseo.metaTitle || page.metaTitle,
    description: page.metaDescription,
    canonical: `https://blezzings.co${capUrl}`,
    breadcrumbs: [
      { name: "Home", url: "https://blezzings.co/" },
      { name: "Our Services", url: `https://blezzings.co${capUrl}` },
      { name: page.title, url: `https://blezzings.co${capUrl}` },
    ],
    faqs: page.faqs,
    service: {
      name: page.title,
      description: page.metaDescription,
      url: `https://blezzings.co${capUrl}`,
    },
  });

  const openBook = (source) => {
    track("book_consultation_open", { source });
    track("service_cta_click", {
      service: page.title,
      source_page: window.location.pathname,
      cta_location: source,
    });
    setBookOpen(true);
  };

  // Challenge cards get a topic-matched visual, never repeated on the page.
  const pick = uniquePicker(
    [image, BAND_IMAGES[capUrl]],
    [serviceImagePool(capUrl.split("/").pop(), image), ALL_IMAGES]
  );
  const challengesWithImages = challenges.map((c) => ({
    ...c,
    image: c.image || pick(imageForChallenge(`${c.title} ${c.body || ""}`)),
  }));
  const explorerWithImages = explorerItems.map((m) => ({
    ...m,
    image: m.link ? pick(imageForPath(m.link.to)) : m.image,
  }));

  return (
    <div data-testid={testid} className="relative">
      <Nav onBook={() => openBook("nav")} />

      <EditorialHero
        testid={`${testid}-hero`}
        eyebrow={pseo.heroH1 ? page.title : "Our Services"}
        title={pseo.heroH1 || page.title}
        description={heroDescription}
        tagline={heroTagline}
        image={image}
        imageAlt={imageAlt}
        primaryLabel={pseo.heroCta || page.heroCta}
        onPrimary={() => openBook(`${testid}-hero`)}
        secondaryLabel="Explore the Capabilities"
        secondaryTo="#capabilities"
      />

      <ChallengeCards
        testid={`${testid}-challenges`}
        badge="The Business Challenge"
        headline={page.challengeLead || page.introLead}
        challenges={challengesWithImages}
        kicker={page.challengeClosing || page.introClosing}
      />

      <div id="capabilities">
        <ServiceExplorer
          testid={`${testid}-explorer`}
          badge="What We Offer"
          headline={page.offersLead}
          intro="Open any capability to see what it covers, what it typically delivers, and where to go deeper."
          items={explorerWithImages}
          deliverablesLabel="Typical deliverables across engagements"
          deliverables={deliverables}
        />
      </div>

      <FrameworkDiagram
        testid={`${testid}-framework`}
        badge="How We Work"
        headline={frameworkHeadline || page.approachLead}
        steps={
          frameworkSteps ||
          page.approach.map((s) => ({ name: s.title, desc: s.body }))
        }
        kicker={frameworkKicker ?? page.approachClosing}
      />

      {/* Quiet cross-link: how engagements unfold */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 -mt-6 pb-2 text-center">
        <Link
          to="/how-we-engage"
          data-testid={`${testid}-how-we-engage-link`}
          className="text-sm text-zinc-500 hover:text-black underline underline-offset-4 decoration-zinc-300 hover:decoration-black transition-colors"
        >
          How engagements work →
        </Link>
      </div>

      <ImageBand
        testid={`${testid}-band`}
        image={BAND_IMAGES[capUrl]}
        alt={`${page.title} — BLEZZINGS`}
        caption={page.title}
      />

      {practiceMagnet && (
        <LeadMagnetSection
          testid="practice-leadmagnet"
          resource={practiceMagnet}
          onOpen={() => setMagnetOpen(true)}
        />
      )}

      <ToolPromo pageUrl={capUrl} testid="practice-tool-promo" />

      {methods?.length > 0 && (
        <ServiceExplorer
          testid={`${testid}-methods`}
          badge="Research Methods"
          headline="How we get to the truth."
          intro="The method follows the question — open each approach to see when and how we use it."
          items={methods.map((m) => ({
            name: m.name,
            outcome: m.body,
            covers: m.list,
          }))}
          tone="alt"
        />
      )}

      {outcomes?.length > 0 && (
        <OutcomeStrip
          testid={`${testid}-outcomes`}
          headline={page.outcomesLead}
          items={outcomes.slice(0, 6)}
          note={page.outcomesClosing}
        />
      )}

      {impact?.length > 0 && (
        <ImpactStrip
          testid={`${testid}-impact`}
          headline={page.impactLead}
          impact={impact}
          note={impactNote ?? page.impactNote}
        />
      )}

      <RelatedLinks
        testid={`${testid}-related`}
        badge="Related Expertise"
        headline={page.relatedLead}
        links={page.related.map((r) => ({ ...r, image: pick(imageForPath(r.to)) }))}
      />

      <FaqSection testid={`${testid}-faqs`} faqs={page.faqs} />

      <RelatedInsights testid="practice-insights" slugs={PRACTICE_INSIGHTS[capUrl.split("/").pop()]} />

      {capUrl === "/capabilities/market-customer-intelligence" && (
        <DiagnosticOffer
          testid="practice-diagnostic"
          onRequest={() => openBook("growth-diagnostic")}
        />
      )}

      <ContextualCTA
        testid={`${testid}-cta`}
        heading={page.ctaHeading}
        body={page.ctaBody}
        label={page.ctaLabel}
        onBook={() => openBook(`${testid}-cta`)}
      />

      <Footer onJoin={() => setJoinOpen(true)} />
      <FloatingActions onBook={() => openBook("floating")} />
      <BookConsultationDialog open={bookOpen} onOpenChange={setBookOpen} service={page.title} />
      <LeadMagnetDialog open={magnetOpen} onOpenChange={setMagnetOpen} resource={practiceMagnet} />
      <JoinUsDialog open={joinOpen} onOpenChange={setJoinOpen} />
    </div>
  );
}
